import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext';
import {
  Building2, Users, AlertTriangle, RefreshCw, Flag, ArrowRight, Activity,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// HONESTY NOTE (EMT SuperAdmin P0 batch, 2026-06-17):
// The previous Control Tower was a large hardcoded-demo monolith (fake tenants like
// TechCorp/HealthCare, fake feature flags, fake rate limits, fake role permissions,
// fake billing ops). This honest rewrite shows ONLY real backend data:
//   - /super-admin/dashboard       (real tenant/user counts, alerts, recent activity)
//   - /super-admin/subscription-plans (real plans)
//   - /super-admin/feature-flags   (real flags)
// Revenue is a flat-rate estimate from the backend (active x $99.99), so it is clearly
// labelled "estimated", not live billing. Tenant/User management delegate to their
// dedicated wired pages. Sections with no backend (billing ops, role permissions, API
// rate limits, tier feature-matrix) are shown as honest "not implemented" — no fake data.

const ControlTower = () => {
  const { superAdmin } = useSuperAdminAuth();
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [plans, setPlans] = useState([]);
  const [flags, setFlags] = useState([]);
  const [ops, setOps] = useState(null); // real Platform Ops projection (read-only)
  const [bedrock, setBedrock] = useState(null); // D1 read-only Bedrock architecture projection
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiRef = useRef(null);
  if (!apiRef.current) {
    const instance = axios.create({ baseURL: BACKEND_URL });
    instance.interceptors.request.use((config) => {
      const token =
        superAdmin?.token ||
        localStorage.getItem('superadmin_session') ||
        localStorage.getItem('superadmin_session_token') ||
        localStorage.getItem('superAdminToken') ||
        localStorage.getItem('superadminToken');
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    apiRef.current = instance;
  }
  const api = apiRef.current;

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [d, p, f, m, b] = await Promise.allSettled([
        api.get('/api/v1/super-admin/dashboard'),
        api.get('/api/v1/super-admin/commercial/plans-summary'),
        api.get('/api/v1/super-admin/runtime-flags'),
        api.get('/api/v1/super-admin/platform-ops'),
        api.get('/api/v1/super-admin/bedrock/architecture'),
      ]);
      if (d.status === 'fulfilled') setDash(d.value.data); else throw d.reason;
      setPlans(p.status === 'fulfilled' ? (p.value.data?.plans || []) : []);
      setFlags(f.status === 'fulfilled' ? (f.value.data?.flags || []) : []);
      setOps(m.status === 'fulfilled' ? (m.value.data || null) : null);
      setBedrock(b.status === 'fulfilled' ? b.value.data : null); // read-only; fail-open to null
    } catch (err) {
      setError(err?.response?.status ? `HTTP ${err.response.status}` : 'network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const fmt = (v) => (v === null || v === undefined ? '—' : String(v));
  const money = (v) => (v === null || v === undefined ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

  const Stat = ({ icon: Icon, label, value, sub, color = 'bg-purple-500' }) => (
    <Card className="bg-white shadow-md">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 mb-1">{label}</p>
            <p className="text-2xl font-bold text-gray-900">{loading ? '…' : value}</p>
            {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
          </div>
          <div className={`${color} p-3 rounded-lg`}><Icon className="h-6 w-6 text-white" /></div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Control Tower</h1>
          <p className="text-gray-600 mt-1">Platform operations, runtime governance, release controls and engine readiness.</p>
          <p className="text-xs text-gray-500 mt-1">
            Plans, pricing &amp; credits →{' '}
            <button type="button" onClick={() => navigate('/superadmin-portal/dashboard/commercial')} className="text-purple-700 hover:underline">Commercial Control</button>
            {' · '}
            <button type="button" onClick={() => navigate('/superadmin-portal/dashboard/status-updates')} className="text-purple-700 hover:underline">Manage Status &amp; Updates</button>
          </p>
        </div>
        <Button onClick={fetchAll} variant="outline" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-red-50 border border-red-200">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          <p className="text-sm text-red-700">Couldn't load platform data ({error}).</p>
          <Button onClick={fetchAll} size="sm" variant="outline" className="ml-auto">Retry</Button>
        </div>
      )}

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="plans">Subscription Plans</TabsTrigger>
          <TabsTrigger value="flags">Feature Flags</TabsTrigger>
          <TabsTrigger value="ops">Platform Ops</TabsTrigger>
          <TabsTrigger value="bedrock">Bedrock Architecture</TabsTrigger>
        </TabsList>

        {/* OVERVIEW — real dashboard data */}
        <TabsContent value="overview" className="space-y-6 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Stat icon={Building2} label="Total Tenants" value={fmt(dash?.total_tenants)} sub={`${fmt(dash?.active_tenants)} active · ${fmt(dash?.trial_tenants)} trial · ${fmt(dash?.suspended_tenants)} suspended`} color="bg-blue-500" />
            <Stat icon={Users} label="Users" value={fmt(dash?.total_users)} sub={`${fmt(dash?.active_users)} active`} color="bg-green-500" />
            <Stat icon={AlertTriangle} label="Critical Alerts" value={fmt(dash?.critical_alerts)} color="bg-red-500" />
          </div>

          {/* Delegate tenant/user management to the dedicated wired pages (no duplicated mock tables) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Card className="bg-white shadow-md">
              <CardContent className="p-5 flex items-center justify-between">
                <div><p className="font-semibold text-gray-900">Tenant Management</p><p className="text-sm text-gray-500">Manage business tenants (live)</p></div>
                <Button variant="outline" onClick={() => navigate('/superadmin-portal/dashboard/tenants')}>Open <ArrowRight className="h-4 w-4 ml-1" /></Button>
              </CardContent>
            </Card>
            <Card className="bg-white shadow-md">
              <CardContent className="p-5 flex items-center justify-between">
                <div><p className="font-semibold text-gray-900">User Management</p><p className="text-sm text-gray-500">Manage platform users (live)</p></div>
                <Button variant="outline" onClick={() => navigate('/superadmin-portal/dashboard/users')}>Open <ArrowRight className="h-4 w-4 ml-1" /></Button>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-white shadow-md">
            <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-lg"><Activity className="h-5 w-5 text-purple-600" />Recent Activity</CardTitle></CardHeader>
            <CardContent>
              {loading ? <p className="text-sm text-gray-400">Loading…</p>
                : (dash?.recent_activities || []).length === 0 ? <p className="text-sm text-gray-500">No recent activity recorded.</p>
                : (
                  <div className="space-y-3">
                    {dash.recent_activities.map((a, i) => (
                      <div key={i} className="flex items-center gap-3 pb-2 border-b border-gray-100 last:border-0">
                        <Activity className="h-4 w-4 text-purple-500 flex-shrink-0" />
                        <span className="text-sm text-gray-800 flex-1 truncate">{a.message || a.type}</span>
                        <span className="text-xs text-gray-400">{a.timestamp || ''}</span>
                      </div>
                    ))}
                  </div>
                )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PLANS — real */}
        <TabsContent value="plans" className="pt-4">
          <Card className="bg-white shadow-md">
            <CardHeader className="pb-3 flex-row items-start justify-between space-y-0">
              <div>
                <CardTitle className="text-lg">Subscription Plans (canonical, read-only)</CardTitle>
                <p className="text-xs text-gray-500 mt-1">Operational summary from the commercial catalog. Edit plans in Commercial Control.</p>
              </div>
              <button type="button" onClick={() => navigate('/superadmin-portal/dashboard/commercial')} className="text-xs text-purple-700 hover:underline whitespace-nowrap">Manage in Commercial Control →</button>
            </CardHeader>
            <CardContent>
              {loading ? <p className="text-sm text-gray-400">Loading…</p>
                : plans.length === 0 ? <p className="text-sm text-gray-500">No published plans in the commercial catalog yet.</p>
                : (
                  <div className="space-y-2">
                    {plans.map((p, i) => (
                      <div key={p.id || i} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div>
                          <span className="font-medium text-gray-900">{p.name || p.id || '—'}</span>
                          <p className="text-xs text-gray-500">
                            {p.version ? `v${p.version} · ` : ''}{p.state || '—'}
                            {` · ${p.published_count || 0} published`}
                            {p.draft_count ? ` · ${p.draft_count} draft` : ''}
                            {p.scheduled_count ? ` · ${p.scheduled_count} scheduled` : ''}
                            {p.subscribed_tenant_count != null ? ` · ${p.subscribed_tenant_count} tenant${p.subscribed_tenant_count === 1 ? '' : 's'}` : ''}
                          </p>
                        </div>
                        <span className="text-sm text-gray-600">{p.price_dollars != null ? `${money(p.price_dollars)}${p.currency ? ` ${p.currency}` : ''}` : '—'}</span>
                      </div>
                    ))}
                  </div>
                )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* FLAGS — real */}
        <TabsContent value="flags" className="pt-4">
          <Card className="bg-white shadow-md">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg"><Flag className="h-5 w-5 text-purple-600" />Runtime Feature Flags (live · read-only)</CardTitle>
              <p className="text-xs text-gray-500 mt-1">Real platform runtime gates. These are environment-driven — changing one requires a redeploy, not a toggle here.</p>
            </CardHeader>
            <CardContent>
              {loading ? <p className="text-sm text-gray-400">Loading…</p>
                : flags.length === 0 ? <p className="text-sm text-gray-500">No runtime flags available.</p>
                : (
                  <div className="space-y-2">
                    {flags.map((f, i) => (
                      <div key={f.key || i} className="flex items-start justify-between py-2 border-b last:border-0 gap-4">
                        <div className="min-w-0">
                          <span className="font-medium text-gray-900">{f.name || f.key}</span>
                          {f.purpose && <p className="text-xs text-gray-500">{f.purpose}</p>}
                          <p className="text-[11px] text-gray-400 mt-0.5">
                            {f.governing_service ? `${f.governing_service} · ` : ''}{f.environment || '—'}
                            {f.deployment_required ? ' · change needs redeploy' : ''}
                            {f.explicitly_set === false ? ' · running on default' : ''}
                          </p>
                          {f.dependency_impact && <p className="text-[11px] text-gray-400">{f.dependency_impact}</p>}
                        </div>
                        <div className="text-right shrink-0">
                          <Badge className={f.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                            {f.enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                          <p className="text-[11px] text-gray-400 mt-1">Last change: {f.last_change || 'Not available'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* OPS — real tier feature matrix from /features/matrix */}
        <TabsContent value="ops" className="pt-4">
          <Card className="bg-white shadow-md">
            <CardHeader className="pb-3"><CardTitle className="text-lg">Platform Ops (live · read-only)</CardTitle></CardHeader>
            <CardContent>
              {loading ? <p className="text-sm text-gray-400">Loading…</p>
                : !ops ? <p className="text-sm text-gray-500">Platform ops projection not available.</p>
                : (
                  <div className="space-y-4 text-sm">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div><p className="text-xs text-gray-500">Environment</p><p className="font-medium">{ops.environment}</p></div>
                      <div><p className="text-xs text-gray-500">Service</p><p className="font-medium break-all">{ops.service}</p></div>
                      <div><p className="text-xs text-gray-500">Revision</p><p className="font-medium break-all">{ops.revision}</p></div>
                      <div><p className="text-xs text-gray-500">Database</p><p className="font-medium">{ops.database?.name} · <span className={ops.database?.connectivity === 'reachable' ? 'text-green-700' : ops.database?.connectivity === 'degraded' ? 'text-red-700' : 'text-gray-500'}>{ops.database?.connectivity}</span></p></div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">D5 reminder scheduler</p>
                      <Badge className={ops.scheduler?.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>{ops.scheduler?.enabled ? 'Enabled' : 'Disabled'}</Badge>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-2">Integration readiness</p>
                      <div className="space-y-1">
                        {(ops.integrations || []).map((it) => (
                          <div key={it.key} className="flex items-center justify-between py-1 border-b last:border-0 gap-4">
                            <div className="min-w-0">
                              <span className="font-medium text-gray-900">{it.name}</span>
                              <p className="text-[11px] text-gray-400">{it.detail}{it.model ? ` · ${it.model}` : ''}{it.webhook_ready !== undefined ? ` · webhook ${it.webhook_ready ? 'ready' : 'missing'}` : ''}{it.real_voice_enabled !== undefined ? ` · real-voice ${it.real_voice_enabled ? 'on' : 'off'}` : ''}</p>
                            </div>
                            <Badge className={it.ready ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>{it.ready ? 'Ready' : 'Not configured'}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                      <div><p className="text-gray-500">Release timestamp</p><p className="text-gray-400">{ops.release_timestamp}</p></div>
                      <div><p className="text-gray-500">Rollback readiness</p><p className="text-gray-400">{ops.rollback_readiness}</p></div>
                      <div><p className="text-gray-500">Recent failures</p><p className="text-gray-400">{ops.recent_controlled_failures}</p></div>
                    </div>
                    {ops.notes && <p className="text-[11px] text-gray-400">{ops.notes}</p>}
                  </div>
                )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* BEDROCK ARCHITECTURE — read-only D1 projection. NEVER mutates; tenant availability is SACP's job. */}
        <TabsContent value="bedrock" className="pt-4 space-y-4">
          {loading ? <p className="text-sm text-gray-400">Loading…</p>
            : !bedrock ? <p className="text-sm text-gray-500">Bedrock architecture projection not available.</p>
            : (
              <>
                <Card className="bg-white shadow-md">
                  <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-lg"><Activity className="h-5 w-5 text-purple-600" />Bedrock Architecture &amp; Integration <span className="text-xs font-normal text-gray-400">(read-only)</span></CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-xs text-gray-500">Platform engine architecture and runtime-integration state. This view is read-only and does <b>not</b> grant tenant access — availability is governed in Commercial Control &amp; Tenant 360. <code>status_updates</code> is the only capability available to all registered Businesses; every other feature is tenant-scoped.</p>
                    <div className="flex flex-wrap gap-2 text-xs">
                      <Badge className="bg-gray-100 text-gray-700">Control plane: {fmt(bedrock.control_plane_posture?.bedrock_control_plane_enabled)}</Badge>
                      <Badge className="bg-gray-100 text-gray-700">Kill switch: {fmt(bedrock.control_plane_posture?.bedrock_kill_switch)}</Badge>
                      <Badge className={(bedrock.validation?.unknown_feature_engine_references?.length || bedrock.validation?.dependency_cycles?.length) ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}>
                        {(bedrock.validation?.unknown_feature_engine_references?.length || bedrock.validation?.dependency_cycles?.length) ? 'Validation issues' : 'References valid'}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white shadow-md">
                  <CardHeader className="pb-2"><CardTitle className="text-base">What these states mean</CardTitle></CardHeader>
                  <CardContent className="text-xs text-gray-600 space-y-1">
                    <p><b>Control plane: false</b> — Bedrock engines observe/hold and do not serve tenant traffic. <b>true</b> — orchestration is active.</p>
                    <p><b>Kill switch: true</b> — all Bedrock execution is force-disabled regardless of other flags (emergency stop).</p>
                    <p><b>Runtime adapter</b> — <span className="text-blue-700">mounted_service / mounted_market_runtime</span>: a real backing service is serving. <span className="text-gray-500">not_connected</span>: source-complete but not mounted (held). <span className="text-amber-700">planned_mock_only</span>: not yet implemented — a mock, not available.</p>
                    <p><b>Control plane (per engine)</b> — <span className="text-gray-500">held_off</span>: the engine is held and does not serve.</p>
                    <p><b>Tenant availability</b> — <span className="text-gray-500">not_tenant_available</span>: not offered to tenants. Tenant access is granted in <button type="button" onClick={() => navigate('/superadmin-portal/dashboard/commercial')} className="text-purple-700 hover:underline">Commercial Control</button> &amp; Tenant 360, never on this read-only view.</p>
                    <p>An engine may be <b>implemented but disabled</b> when its source exists and tests pass but a governed flag holds it off — it is not sold or assigned while disabled.</p>
                    <p className="text-amber-700"><b>Location/ETA</b> — the generic engine is now <b>implemented (held_constitutional_source)</b>: real <code>LocationEtaEngine</code> over a provider seam. The real routing provider is <b>not configured</b>, so live ETA is <b>not available</b> yet (truthful UNAVAILABLE, never a fake ETA). Not shown as available until a provider is activated.</p>
                  </CardContent>
                </Card>

                <Card className="bg-white shadow-md">
                  <CardHeader className="pb-2"><CardTitle className="text-base">Constitutional Engines <span className="text-xs font-normal text-gray-400">({(bedrock.constitutional_engines || []).length})</span></CardTitle></CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto"><table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b"><tr>
                        {['Engine', 'Contract', 'Router', 'Runtime adapter', 'Control plane', 'Tenant availability', 'Owned features'].map((h) => (
                          <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>
                        ))}
                      </tr></thead>
                      <tbody className="divide-y divide-gray-100">
                        {(bedrock.constitutional_engines || []).map((e) => {
                          const dim = e.dimensions || {};
                          return (
                            <tr key={e.engine_key} className="hover:bg-gray-50 align-top">
                              <td className="px-3 py-2"><span className="font-medium text-gray-900">{e.display_name}</span><div className="text-xs text-gray-400">{e.category}</div></td>
                              <td className="px-3 py-2 text-xs text-gray-600">{fmt(dim.contract_shape)}</td>
                              <td className="px-3 py-2 text-xs text-gray-600">{fmt(dim.router_registration)}</td>
                              <td className="px-3 py-2 text-xs">
                                <Badge className={dim.runtime_adapter === 'mounted_service' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}>{fmt(dim.runtime_adapter)}</Badge>
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-600">{fmt(dim.control_plane)}</td>
                              <td className="px-3 py-2 text-xs"><Badge className="bg-gray-100 text-gray-600">{fmt(dim.tenant_availability)}</Badge></td>
                              <td className="px-3 py-2 text-xs text-gray-600">{(e.owned_features || []).map((o) => o.feature_key).join(', ') || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table></div>
                    <p className="text-xs text-gray-400 mt-2">Honest state labels — a source-complete engine is not tenant-available; “unknown_or_not_found” test evidence is never reported as tested.</p>
                  </CardContent>
                </Card>

                <Card className="bg-white shadow-md">
                  <CardHeader className="pb-2"><CardTitle className="text-base">Mounted Market Runtime Adapters</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    {(bedrock.mounted_runtime_adapters || []).map((a) => (
                      <div key={a.adapter_key} className="flex items-start justify-between gap-3 py-2 border-b last:border-0">
                        <div><span className="font-medium text-gray-900 text-sm">{a.display_name}</span><div className="text-xs text-gray-400">{a.constitutional_engine_key || a.adapter_key}</div></div>
                        <Badge className={a.state === 'mounted_market_runtime' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}>{fmt(a.state)}</Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </>
            )}
        </TabsContent>

      </Tabs>
    </div>
  );
};

export default ControlTower;
